import { removeDopplerRadarImageNoise } from './radar-utils.mjs';
import { RADAR_FULL_SIZE, RADAR_FINAL_SIZE, RADAR_SOURCE_SIZE } from './radar-constants.mjs';

// YEETLAB radar crop: the archive frame (n0r_YYYYMMDDHHMM.png) is 6000x2600 at 0.01 degree per
// pixel, ~490 KB, and used to be downloaded whole six times per visit just to cut a ~240x163
// window out of it. Iowa State serves the same data cropped from their time-enabled map service
// (WMS), so only the window is fetched (a few KB). The whole frame is still the fallback.
const ARCHIVE_SIZE = { width: 6000, height: 2600 };
// the archive's world file puts the CENTRE of its upper-left pixel at -126.0 / 50.0
const ARCHIVE_WEST = -126;
const ARCHIVE_NORTH = 50;
const ARCHIVE_STEP = 0.01;
// (mode=8bit is the same colours as a palette, a third smaller than the default RGBA)
const WMS_PATH = '/cgi-bin/wms/nexrad/n0r-t.cgi?SERVICE=WMS&VERSION=1.1.1&REQUEST=GetMap&LAYERS=nexrad-n0r-wmst&STYLES=&SRS=EPSG:4326&FORMAT=image/png;%20mode=8bit&TRANSPARENT=true';
// a thumbnail of the whole map, used to tell an empty sky from a frame the service does not have yet
const PROBE_SIZE = { width: 600, height: 260 };

// The old path stretched the whole archive frame onto the RADAR_FULL_SIZE canvas with smoothing
// off, so canvas pixel n showed archive pixel floor((n + 0.5) * archive / canvas); Chrome
// resolves an exact tie (every 17th column) to the lower pixel. This is that rule in integers,
// so the crop picks the very pixels the old path did, and picks them the same in every browser.
const archiveIndex = (n, archive, canvas) => Math.ceil(((2 * n + 1) * archive) / (2 * canvas)) - 1;

const makeCanvas = (width, height, options) => {
	const canvas = document.createElement('canvas');
	canvas.width = width;
	canvas.height = height;
	const context = canvas.getContext('2d', options);
	context.imageSmoothingEnabled = false;
	return context;
};

// the part of the crop that lies on the canvas, and the archive pixels it samples
const getCropRegion = (radarSource) => {
	const x0 = Math.max(0, radarSource.x);
	const x1 = Math.min(RADAR_FULL_SIZE.width, radarSource.x + radarSource.width);
	const y0 = Math.max(0, radarSource.y);
	const y1 = Math.min(RADAR_FULL_SIZE.height, radarSource.y + radarSource.height);
	if (x1 <= x0 || y1 <= y0) return null;
	return {
		x0,
		y0,
		x1,
		y1,
		col0: archiveIndex(x0, ARCHIVE_SIZE.width, RADAR_FULL_SIZE.width),
		col1: archiveIndex(x1 - 1, ARCHIVE_SIZE.width, RADAR_FULL_SIZE.width),
		row0: archiveIndex(y0, ARCHIVE_SIZE.height, RADAR_FULL_SIZE.height),
		row1: archiveIndex(y1 - 1, ARCHIVE_SIZE.height, RADAR_FULL_SIZE.height),
	};
};

// Ask for exactly archive pixels col0..col1 x row0..row1, one output pixel per archive pixel.
// The box is shifted half a pixel so each output pixel centre sits on an archive pixel centre
// (the world file convention above). Edge-aligned boxes land on ties, and the server then
// sometimes returns the whole image one row off.
const getMapUrl = (host, timestamp, box, size) => {
	const half = ARCHIVE_STEP / 2;
	const west = ARCHIVE_WEST + box.col0 * ARCHIVE_STEP - half;
	const east = ARCHIVE_WEST + (box.col1 + 1) * ARCHIVE_STEP - half;
	const north = ARCHIVE_NORTH - box.row0 * ARCHIVE_STEP + half;
	const south = ARCHIVE_NORTH - (box.row1 + 1) * ARCHIVE_STEP + half;
	const [, year, month, day, hour, minute] = timestamp;
	return `https://${host}${WMS_PATH}&BBOX=${west.toFixed(3)},${south.toFixed(3)},${east.toFixed(3)},${north.toFixed(3)}`
		+ `&WIDTH=${size.width}&HEIGHT=${size.height}&TIME=${year}-${month}-${day}T${hour}:${minute}:00Z`;
};

// download a picture from the map service; throws unless it is a picture of the size asked for
// (a bad request comes back as XML with a 200 status)
const fetchMap = async (url, size, cache) => {
	const response = await fetch(url, { cache });
	if (!response.ok) throw new Error(`Unable to fetch radar crop ${response.status} ${response.statusText}`);
	if (!response.headers.get('content-type')?.startsWith('image/')) throw new Error('Radar crop is not an image');
	const blob = await response.blob();
	const bitmap = await createImageBitmap(blob);
	if (bitmap.width !== size.width || bitmap.height !== size.height) {
		throw new Error(`Radar crop is ${bitmap.width}x${bitmap.height}, not the size asked for`);
	}
	return { blob, bitmap };
};

const readPixels = (bitmap) => {
	const context = makeCanvas(bitmap.width, bitmap.height, { willReadFrequently: true });
	context.drawImage(bitmap, 0, 0);
	return context.getImageData(0, 0, bitmap.width, bitmap.height).data;
};

// any echo at all (the picture is black or see-through where the sky is clear)
const hasEcho = (pixels) => {
	for (let i = 0; i < pixels.length; i += 4) {
		if (pixels[i + 3] && (pixels[i] || pixels[i + 1] || pixels[i + 2])) return true;
	}
	return false;
};

// The map service answers a time it has no picture for with a valid, EMPTY image, and a frame
// is listed in the archive a little before the service has it. So an empty crop proves nothing
// until the whole map for that time shows echoes somewhere.
const frameIsPublished = async (host, timestamp, cache) => {
	const whole = {
		col0: 0, col1: ARCHIVE_SIZE.width - 1, row0: 0, row1: ARCHIVE_SIZE.height - 1,
	};
	const { bitmap } = await fetchMap(getMapUrl(host, timestamp, whole, PROBE_SIZE), PROBE_SIZE, cache);
	return hasEcho(readPixels(bitmap));
};

// sample the cropped frame onto the crop canvas the way the old whole-frame stretch did
const drawCrop = (croppedRadarContext, native, nativeWidth, region, radarSource) => {
	const cropped = croppedRadarContext.createImageData(radarSource.width, radarSource.height);
	for (let y = region.y0; y < region.y1; y += 1) {
		const nativeRow = (archiveIndex(y, ARCHIVE_SIZE.height, RADAR_FULL_SIZE.height) - region.row0) * nativeWidth;
		const croppedRow = (y - radarSource.y) * radarSource.width;
		for (let x = region.x0; x < region.x1; x += 1) {
			const from = (nativeRow + archiveIndex(x, ARCHIVE_SIZE.width, RADAR_FULL_SIZE.width) - region.col0) * 4;
			const to = (croppedRow + x - radarSource.x) * 4;
			cropped.data[to] = native[from];
			cropped.data[to + 1] = native[from + 1];
			cropped.data[to + 2] = native[from + 2];
			cropped.data[to + 3] = native[from + 3];
		}
	}
	croppedRadarContext.putImageData(cropped, 0, 0);
};

// the old path: download the whole frame, stretch it onto the canvas, cut the window out
const drawWholeFrame = async (croppedRadarContext, blob, radarSource) => {
	const radarContext = makeCanvas(RADAR_FULL_SIZE.width, RADAR_FULL_SIZE.height);
	const radarImgElement = await createImageBitmap(blob);
	// draw the entire image
	radarContext.clearRect(0, 0, RADAR_FULL_SIZE.width, RADAR_FULL_SIZE.height);
	radarContext.drawImage(radarImgElement, 0, 0, RADAR_FULL_SIZE.width, RADAR_FULL_SIZE.height);

	// crop the radar image without scaling
	const { canvas } = croppedRadarContext;
	croppedRadarContext.drawImage(radarContext.canvas, radarSource.x, radarSource.y, canvas.width, canvas.height, 0, 0, canvas.width, canvas.height);
};

// process a single radar image and place it on the provided canvas
// YEETLAB: finalSize/sourceSize are now passed in so the radar can be rendered
// taller than the 367px band. sourceBlob lets a re-render at a new height reuse
// the already-downloaded frame instead of hitting the network again.
// A stored crop only fits the window it was fetched for (sourceBlobKey says which);
// a stored whole frame fits any window.
// fresh marks the newest frames, which the map service may not have yet: the browser is
// told not to keep their pictures, so an empty answer given too early is not replayed for a day.
const processRadar = async (data) => {
	const {
		url, RADAR_HOST, OVERRIDES, radarSourceXY,
		finalSize = RADAR_FINAL_SIZE,
		sourceSize = RADAR_SOURCE_SIZE,
		sourceBlob,
		sourceBlobKey,
		fresh = false,
	} = data;

	// calculate offsets and sizes
	const radarSource = {
		width: sourceSize.width,
		height: sourceSize.height,
		x: Math.round(radarSourceXY.x / 2),
		y: Math.round(radarSourceXY.y / 2),
	};

	// create radar context for manipulation
	const croppedRadarContext = makeCanvas(radarSource.width, radarSource.height);

	const timestamp = url.match(/_(\d{4})(\d\d)(\d\d)(\d\d)(\d\d)\./);
	const region = getCropRegion(radarSource);
	const regionKey = region && `${region.col0}-${region.col1}-${region.row0}-${region.row1}`;

	// get the image: the cropped picture when the server can give one, else the whole frame
	let blob = sourceBlob;
	let blobKey = sourceBlobKey;
	// a window entirely off the map is drawn as nothing, as the old path did
	let drawn = !region;
	// a custom radar host is a mirror of the archive only, which has no map service
	if (!drawn && timestamp && !OVERRIDES.RADAR_HOST && blobKey !== 'full') {
		try {
			let bitmap;
			let native;
			if (blob && blobKey === regionKey) {
				bitmap = await createImageBitmap(blob);
				native = readPixels(bitmap);
			} else {
				const cache = fresh ? 'no-store' : 'default';
				const size = { width: region.col1 - region.col0 + 1, height: region.row1 - region.row0 + 1 };
				({ blob, bitmap } = await fetchMap(getMapUrl(RADAR_HOST, timestamp, region, size), size, cache));
				blobKey = regionKey;
				native = readPixels(bitmap);
				// an empty crop only counts if the service really has the frame
				if (!hasEcho(native) && !await frameIsPublished(RADAR_HOST, timestamp, cache)) {
					throw new Error('The radar map service does not have this frame yet');
				}
			}
			drawCrop(croppedRadarContext, native, bitmap.width, region, radarSource);
			drawn = true;
		} catch (_error) {
			// fall back to the whole frame
			blob = undefined;
			blobKey = undefined;
		}
	}
	if (!drawn) {
		if (blobKey !== 'full') {
			const modifiedRadarUrl = OVERRIDES.RADAR_HOST ? url.replace(RADAR_HOST, OVERRIDES.RADAR_HOST) : url;
			const radarResponse = await fetch(modifiedRadarUrl);
			// test response
			if (!radarResponse.ok) throw new Error(`Unable to fetch radar error ${radarResponse.status} ${radarResponse.statusText} from ${radarResponse.url}`);
			blob = await radarResponse.blob();
			blobKey = 'full';
		}
		await drawWholeFrame(croppedRadarContext, blob, radarSource);
	}

	// clean the image
	removeDopplerRadarImageNoise(croppedRadarContext);

	// stretch the radar image
	const stretchContext = makeCanvas(finalSize.width, finalSize.height, { willReadFrequently: true });
	stretchContext.drawImage(croppedRadarContext.canvas, 0, 0, radarSource.width, radarSource.height, 0, 0, finalSize.width, finalSize.height);

	return { dataURL: stretchContext.canvas.toDataURL(), blob, blobKey };
};

export default processRadar;
